import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, CardBody, CardFooter, CardHeader, Col, CustomInput, FormGroup, Input, Label, Row, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CertificateThirdPartySeriesProductSearch from "./CertificateThirdPartySeriesProductSearch";
import { useService } from "../../context/ServiceContext";
import { ExistingCertificateSummary } from "../../views/Certificates/Edit";
import { fireAlert, fireConfirm } from "../../utils/alerts";
import CollapsibleCard from "../CollapsibleCard";
import { useDropzone } from "react-dropzone";
import { MultiSelect } from "react-multi-select-component";
import ErrorAlertContent from "../ErrorAlertContent";
import LoadedContent from "../LoadedContent";
import SubmitButton from "../SubmitButton";
import { groupArchivedFiles, appendCertificateFiles } from "./thirdPartyCertificateFiles";

function CertificateThirdPartyProductSelection({ history, existingCertificate }) {
  const intl = useIntl();

  const customValueRenderer = (selected, _options) => {
    return selected.map(({ label }) => label).join(",");
  };

  const localisedStrings = {
    allItemsAreSelected: "All items are selected.",
    clearSearch: "Clear Search",
    clearSelected: "Clear Selected",
    noOptions: "No options",
    search: "Search",
    selectAll: "Select All",
    selectAllFiltered: "Select All (Filtered)",
    selectSomeItems: intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" }),
  };

  const convertDay = (date) => {
    const today = date ?? new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const {
    certificateManufacturersProvider,
    certificateEntitiesProvider,
    certificateDocumentTypesProvider,
    certificateRegulationsByDocumentTypesProvider,
    certificateThirdPartyStore,
    certificateThirdPartyUpdateStore,
    certificateLanguagesProvider,
    certificateGenerateProvider,
    certificateDocumentTypeStore,
    certificateRegulationStore,
    certificateDocumentTypeExistenceProvider,
    certificateRegulationExistenceProvider,
    certificateLegislationTypesProvider,
    certificateNotaCodeProvider,
    certificateNotaNameProvider,
  } = useService();

  const [languages, setLanguages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [entities, setEntities] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState(null);
  const [submittedCompany, setSubmittedCompany] = useState(null);

  const [originalLegislations, setOriginalLegislations] = useState([]);
  const [legislations, setLegislations] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [displayRegulations, setDisplayRegulations] = useState([]);
  const [allRegulationsLegislations, setAllRegulationsLegislations] = useState([]);

  const [regulationModalOpen, setRegulationModalOpen] = useState(false);
  const [documentTypeModalOpen, setDocumentTypeModalOpen] = useState(false);

  const toggleRegulationModal = () => setRegulationModalOpen(!regulationModalOpen);
  const toggleDocumentTypeModal = () => setDocumentTypeModalOpen(!documentTypeModalOpen);

  const [tickedLegislations, setTickedLegislations] = useState([]);

  const [codeExists, setCodeExists] = useState(false);
  const [nameExists, setNameExists] = useState(false);

  const [name, setName] = useState(existingCertificate?.name ?? "");
  const [notes, setNotes] = useState(existingCertificate?.notes ?? "");
  const [code, setCode] = useState(existingCertificate?.code ?? "");
  const [active, setActive] = useState(existingCertificate?.active ?? true);
  const [typeOfCertificate, setTypeOfCertificate] = useState(existingCertificate?.typeOfCertificate ?? 0);

  const [selectedCompany, setSelectedCompany] = useState(existingCertificate?.company ?? "");
  const [selectedDocumentType, setSelectedDocumentType] = useState(existingCertificate?.typeOfDocumentCERT ?? 0);

  const [customDocumentTypeName, setCustomDocumentTypeName] = useState("");
  const [customDocumentTypeDescription, setCustomDocumentTypeDescription] = useState("");
  const [documentTypeExists, setDocumentTypeExists] = useState("");

  const [customRegulationName, setCustomRegulationName] = useState("");
  const [customRegulationDescription, setCustomRegulationDescription] = useState("");
  const [customRegulationType, setCustomRegulationType] = useState(0);
  const [customRegulationValidFrom, setCustomRegulationValidFrom] = useState();
  const [customRegulationValidTo, setCustomRegulationValidTo] = useState();
  const [customRegulationAppliesTo, setCustomRegulationAppliesTo] = useState([]);
  const [regulationExists, setRegulationExists] = useState("");
  const [legislationTypes, setLegislationTypes] = useState([]);

  const [selectedLegislations, setSelectedLegislations] = useState([]);
  const [selectedRegulations, setSelectedRegulations] = useState([]);

  const [certificateEntity, setCertificateEntity] = useState(existingCertificate?.certificateEntity ?? ""); // free text
  const [certificateNumber, setCertificateNumber] = useState(existingCertificate?.certificateNumber ?? ""); // free text
  const [certificateNameOf, setCertificateNameOf] = useState(existingCertificate?.certificateNameOf ?? ""); // free text
  const [certificateValidFrom, setCertificateValidFrom] = useState(); // date
  const [certificateLastIssued, setCertificateLastIssued] = useState(); // date
  const [certificateValidityPeriod, setCertificateValidityPeriod] = useState(existingCertificate?.certificateValidityPeriod ?? ""); // free text number
  const [certificateValidTo, setCertificateValidTo] = useState(); // date
  const [certificateManufacturingValidity, setCertificateManufacturingValidity] = useState(); // date
  const [certificateWarningPeriod, setCertificateWarningPeriod] = useState(existingCertificate?.certificateWarningPeriod ?? ""); // free text number
  const [certificateWarningPeriodType, setCertificateWarningPeriodType] = useState(existingCertificate?.certificateWarningPeriodType ?? "days"); // dropdown with days/weeks/months
  const [certificateFiles, setCertificateFiles] = useState(() => groupArchivedFiles(existingCertificate?.uploadedCertificates ?? []));
  const {
    getRootProps: getRootPropsCertificate,
    getInputProps: getInputPropsCertificate,
    open: openCertificate,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: { "application/pdf": [".pdf"] },
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        language: languages.slice(0, 1),
        id: `${file.path}-${file.size}-${Date.now()}`,
        number: "",
      }));

      setCertificateFiles((prev) => [...prev, ...newFiles]);
    },
  });
  const removeCertificateFile = (id) => {
    setCertificateFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const updateCertificateFileLanguage = (id, newLanguage) => {
    setCertificateFiles((prev) => prev.map((item) => (item.id === id ? { ...item, language: newLanguage } : item)));
  };
  const updateCertificateFileCertificateNumber = (id, number) => {
    setCertificateFiles((prev) => prev.map((item) => (item.id === id ? { ...item, number: number } : item)));
  };
  const certificateFileItems = certificateFiles.map(({ file, archive, language, number, id }) => (
    <li key={id} className="d-flex align-items-center mb-2" style={{ gap: 30 }}>
      <div style={{ width: "35%" }}>
        <strong>
          {file ? file.name : archive.pdf && archive.pdf !== "-" ? archive.pdf : intl.formatMessage({ id: "certificates.no_pdf_available", defaultMessage: "[No PDF available]" })}
        </strong>
        {file && <> - {file.size.toLocaleString()} bytes</>}
      </div>
      <div style={{ width: 300 }}>
        <MultiSelect
          valueRenderer={customValueRenderer}
          options={languages}
          value={language.map((selected) => languages.find((option) => option.value === selected.value) ?? selected)}
          hasSelectAll={true}
          onChange={(e) => updateCertificateFileLanguage(id, e)}
          labelledBy="Select"
          overrideStrings={localisedStrings}
        />
      </div>

      <Input
        type="text"
        value={number}
        readOnly={!!archive}
        onChange={(e) => updateCertificateFileCertificateNumber(id, e.target.value)}
        placeholder={intl.formatMessage({ id: "certificates.certificate_number", defaultMessage: "Certificate Number" })}
        style={{ width: "auto" }}
        className={!number ? "is-invalid" : ""}
      />

      {archive?.pdf && archive.pdf !== "-" && (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => generate(existingCertificate.company, existingCertificate.series, existingCertificate.languageId, 0, archive.idTipoDocumental, archive.pdf)}
        >
          <FormattedMessage id="app.view" defaultMessage="View" />
        </button>
      )}

      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeCertificateFile(id)}>
        {intl.formatMessage({ id: "app.remove", defaultMessage: "Remove" })}
      </button>
    </li>
  ));

  const [reportEntity, setReportEntity] = useState(existingCertificate?.reportEntity ?? "");
  const [reportNumber, setReportNumber] = useState(existingCertificate?.reportNumber ?? "");
  const [reportNameOf, setReportNameOf] = useState(existingCertificate?.reportNameOf ?? "");
  const [reportValidFrom, setReportValidFrom] = useState();
  const [reportLastIssued, setReportLastIssued] = useState();
  const [reportValidityPeriod, setReportValidityPeriod] = useState(existingCertificate?.reportValidityPeriod ?? "");
  const [reportValidTo, setReportValidTo] = useState();
  const [reportFiles, setReportFiles] = useState(() => groupArchivedFiles(existingCertificate?.uploadedReports ?? []));
  const {
    getRootProps: getRootPropsReport,
    getInputProps: getInputPropsReport,
    open: openReport,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: { "application/pdf": [".pdf"] },
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        language: languages.slice(0, 1),
        id: `${file.path}-${file.size}-${Date.now()}`,
        number: "",
      }));

      setReportFiles((prev) => [...prev, ...newFiles]);
    },
  });

  const removeReportFile = (id) => {
    setReportFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const updateReportFileLanguage = (id, newLanguage) => {
    setReportFiles((prev) => prev.map((item) => (item.id === id ? { ...item, language: newLanguage } : item)));
  };
  const updateReportFileCertificateNumber = (id, number) => {
    setReportFiles((prev) => prev.map((item) => (item.id === id ? { ...item, number: number } : item)));
  };
  const reportFileItems = reportFiles.map(({ file, archive, language, number, id }) => (
    <li key={id} className="d-flex align-items-center mb-2" style={{ gap: 30 }}>
      <div style={{ width: "35%" }}>
        <strong>
          {file ? file.name : archive.pdf && archive.pdf !== "-" ? archive.pdf : intl.formatMessage({ id: "certificates.no_pdf_available", defaultMessage: "[No PDF available]" })}
        </strong>
        {file && <> - {file.size.toLocaleString()} bytes</>}
      </div>

      <div style={{ width: 300 }}>
        <MultiSelect
          valueRenderer={customValueRenderer}
          options={languages}
          value={language.map((selected) => languages.find((option) => option.value === selected.value) ?? selected)}
          hasSelectAll={true}
          onChange={(e) => updateReportFileLanguage(id, e)}
          labelledBy="Select"
          overrideStrings={localisedStrings}
        />
      </div>

      <Input
        type="text"
        value={number}
        readOnly={!!archive}
        onChange={(e) => updateReportFileCertificateNumber(id, e.target.value)}
        placeholder={intl.formatMessage({ id: "certificates.report_number", defaultMessage: "Report Number" })}
        style={{ width: "auto" }}
        className={!number ? "is-invalid" : ""}
      />

      {archive?.pdf && archive.pdf !== "-" && (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => generate(existingCertificate.company, existingCertificate.series, existingCertificate.languageId, 0, archive.idTipoDocumental, archive.pdf)}
        >
          <FormattedMessage id="app.view" defaultMessage="View" />
        </button>
      )}

      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeReportFile(id)}>
        {intl.formatMessage({ id: "app.remove", defaultMessage: "Remove" })}
      </button>
    </li>
  ));

  const [selectedSeries, setSelectedSeries] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [validationCheck, setValidationCheck] = useState(false);

  async function getDocumentTypes(customDocumentTypeName) {
    const documentTypes = await certificateDocumentTypesProvider.get();
    setDocumentTypes(
      documentTypes.map((a) => {
        return {
          label: a.tipoDocumental,
          value: a.idTipoDocumental,
        };
      }) ?? [],
    );

    if (customDocumentTypeName) {
      const dt = documentTypes.find((d) => d.tipoDocumental == customDocumentTypeName);
      if (dt) {
        setSelectedDocumentType(dt.idTipoDocumental);
      }
    } else if (selectedDocumentType == 0) {
      setSelectedDocumentType(documentTypes[0].idTipoDocumental);
    }
  }

  async function getLegislationTypes() {
    const legislationTypes = await certificateLegislationTypesProvider.get();
    setLegislationTypes(legislationTypes);
    setCustomRegulationType(legislationTypes[0].idTipoRegulacion);
  }

  useEffect(() => {
    async function getData() {
      try {
        const languages = await certificateLanguagesProvider.get();

        setLanguages(
          languages.map((a) => {
            return {
              label: a.idioma,
              value: a.idIdioma,
            };
          }) ?? [],
        );

        //setLanguages(languages ?? []);
      } catch (error) {}
    }
    if (languages.length === 0) getData();
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        const companies = await certificateManufacturersProvider.get();
        setCompanies(companies);

        const entities = await certificateEntitiesProvider.get();
        setEntities(entities);

        await getDocumentTypes();

        getLegislationTypes();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        if (existingCertificate == null) {
          setDisplayRegulations([]);
          setSelectedLegislations([]);
          setSelectedRegulations([]);
          setTickedLegislations([]);
        }

        const allRegulationsLegislations = await certificateRegulationsByDocumentTypesProvider.get(selectedDocumentType);
        setAllRegulationsLegislations(allRegulationsLegislations);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    if (selectedDocumentType > 0) getData();
  }, [selectedDocumentType]);

  useEffect(() => {
    async function getData() {
      try {
        const legislations = allRegulationsLegislations.filter((r) => r.idTipoRegulacion == 2);
        const sources = [existingCertificate?.regs].filter(Boolean);

        for (const src of sources) {
          const clonedLegislations = src.filter((r) => r.idTipoRegulacion == 2);
          for (const clonedLegislation of clonedLegislations) {
            if (!legislations.some((r) => r.idRegulacion == clonedLegislation.idRegulacion)) {
              legislations.push({ ...clonedLegislation, missing: true });
            }
          }
        }

        setLegislations(legislations);

        if (legislations.length == 0) {
          const displayRegulations = allRegulationsLegislations.filter((r) => r.idTipoRegulacion != 2);
          setDisplayRegulations(displayRegulations);
        } else {
          const parentless = allRegulationsLegislations.filter((r) => r.idTipoRegulacion != 2 && r.idRegulacionParent == r.idRegulacion);
          setDisplayRegulations(parentless);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    if (selectedDocumentType > 0) getData();
  }, [allRegulationsLegislations]);

  useEffect(() => {
    async function getData() {
      try {
        const regulations = allRegulationsLegislations.filter((r) => tickedLegislations.includes(r.idRegulacionParent) && r.idTipoRegulacion != 2);

        const sources = [existingCertificate?.regs].filter(Boolean);

        for (const src of sources) {
          const clonedRegulations = src.filter((r) => r.idTipoRegulacion != 2);
          for (const clonedRegulation of clonedRegulations) {
            if (!regulations.some((r) => r.idRegulacion == clonedRegulation.idRegulacion)) {
              regulations.push({ ...clonedRegulation, missing: true });
            }
          }
        }

        //setSelectedRegulations(selectedRegulations.filter((s) => regulations.some((r) => r.idRegulacion == s)));

        setRegulations(regulations);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    if (selectedDocumentType > 0) getData();
  }, [selectedLegislations, tickedLegislations]);

  useEffect(() => {
    async function getData() {
      try {
        if (tickedLegislations.length == 0) {
          const parentless = allRegulationsLegislations.filter((r) => r.idTipoRegulacion != 2 && r.idRegulacionParent == r.idRegulacion);
          setDisplayRegulations(parentless);
        } else {
          const displayRegulations = allRegulationsLegislations.filter((r) => tickedLegislations.includes(r.idRegulacionParent) && r.idTipoRegulacion != 2);
          setDisplayRegulations(displayRegulations);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, [tickedLegislations]);

  useEffect(() => {
    if (!existingCertificate) return;

    const regs = [existingCertificate?.regs].filter(Boolean);

    if (regs?.length > 0) {
      setSelectedLegislations(regs[0].filter((r) => r.idTipoRegulacion == 2).map((r) => r.idRegulacion));
      setSelectedRegulations(regs[0].filter((r) => r.idTipoRegulacion != 2).map((r) => r.idRegulacion));
    }

    const source = existingCertificate;

    if (source?.certificateValidFrom != null) {
      setCertificateValidFrom(convertDay(new Date(source.certificateValidFrom)));
    }
    if (source?.certificateLastIssued != null) {
      setCertificateLastIssued(convertDay(new Date(source.certificateLastIssued)));
    }
    if (source?.certificateValidTo != null) {
      setCertificateValidTo(convertDay(new Date(source.certificateValidTo)));
    }
    if (source?.certificateManufacturingValidity != null) {
      setCertificateManufacturingValidity(convertDay(new Date(source.certificateManufacturingValidity)));
    }

    if (source?.reportValidFrom != null) {
      setReportValidFrom(convertDay(new Date(source.reportValidFrom)));
    }
    if (source?.reportLastIssued != null) {
      setReportLastIssued(convertDay(new Date(source.reportLastIssued)));
    }
    if (source?.reportValidTo != null) {
      setReportValidTo(convertDay(new Date(source.reportValidTo)));
    }
  }, [existingCertificate]);

  async function onLegislationClick(id, remove) {
    if (remove) {
      setSelectedLegislations(selectedLegislations.filter((s) => s != id));
    } else {
      let legislation = [...tickedLegislations];
      if (legislation.includes(id)) legislation = legislation.filter((s) => s != id);
      else {
        legislation = legislation.concat(id);

        let selectedLegislation = [...selectedLegislations];
        if (!selectedLegislation.includes(id)) {
          selectedLegislation = selectedLegislation.concat(id);
          setSelectedLegislations(selectedLegislation);
        }
      }

      setTickedLegislations(legislation);
    }
  }

  async function onRegulationClick(id) {
    let regulations = [...selectedRegulations];
    if (regulations.includes(id)) regulations = regulations.filter((s) => s != id);
    else regulations = regulations.concat(id);

    setSelectedRegulations(regulations);
  }

  async function updateCertificate() {
    if (!isValid()) return;

    setIsSaving(true);

    try {
      const formData = new FormData();

      formData.append("seriesComercial", existingCertificate.series);
      formData.append("language", existingCertificate.languageId);
      formData.append("documentClass", existingCertificate.documentClass);
      formData.append("documentReference", existingCertificate.documentReference);
      formData.append("idNotaCert", existingCertificate.idNotaCert);

      formData.append("typeOfCertificate", typeOfCertificate);
      formData.append("company", selectedCompany);
      formData.append("idTipoDocumental", selectedDocumentType.toString());

      formData.append("name", name);
      formData.append("notes", notes || "");
      formData.append("code", code);
      formData.append("active", active ? "true" : "false");

      const selectedLegislationsFull = allRegulationsLegislations.filter((l) => selectedLegislations.includes(l.idRegulacion));
      const selectedRegulationsFull = allRegulationsLegislations.filter((l) => selectedRegulations.includes(l.idRegulacion));

      const regs = [existingCertificate?.regs].filter(Boolean);
      const oldLegislations = regs?.length > 0 ? regs[0].filter((r) => r.idTipoRegulacion == 2).filter((l) => selectedLegislations.includes(l.idRegulacion)) : [];
      const oldRegulations = regs?.length > 0 ? regs[0].filter((r) => r.idTipoRegulacion != 2).filter((l) => selectedRegulations.includes(l.idRegulacion)) : [];

      const mergedLegislationsFull = [...selectedLegislationsFull, ...oldLegislations.filter((ol) => !selectedLegislationsFull.some((sl) => sl.idRegulacion === ol.idRegulacion))];

      const mergedRegulationsFull = [...selectedRegulationsFull, ...oldRegulations.filter((or) => !selectedRegulationsFull.some((sr) => sr.idRegulacion === or.idRegulacion))];

      formData.append("legislations", JSON.stringify(mergedLegislationsFull ?? []));
      formData.append("regulations", JSON.stringify(mergedRegulationsFull ?? []));

      if (certificateNameOf) {
        formData.append("certificateEntity", certificateEntity || "");
        //formData.append("certificateNumber", certificateNumber || "");
        formData.append("certificateNameOf", certificateNameOf || "");
        formData.append("certificateValidFrom", certificateValidFrom == null || certificateValidFrom == "" ? "" : new Date(certificateValidFrom).toISOString());
        formData.append("certificateLastIssued", certificateLastIssued == null || certificateLastIssued == "" ? "" : new Date(certificateLastIssued).toISOString());
        formData.append("certificateValidityPeriod", certificateValidityPeriod || "");
        formData.append("certificateValidTo", certificateValidTo == null || certificateValidTo == "" ? "" : new Date(certificateValidTo).toISOString());
        formData.append(
          "certificateManufacturingValidity",
          certificateManufacturingValidity == null || certificateManufacturingValidity == "" ? "" : new Date(certificateManufacturingValidity).toISOString(),
        );
        formData.append("certificateWarningPeriod", certificateWarningPeriod || 0);
        formData.append("certificateWarningPeriodType", certificateWarningPeriodType);
      }

      appendCertificateFiles(formData, certificateFiles, "certificate", true);

      if (reportNameOf) {
        formData.append("reportEntity", reportEntity || "");
        //formData.append("reportNumber", reportNumber || "");
        formData.append("reportNameOf", reportNameOf || "");
        formData.append("reportValidFrom", reportValidFrom == null || reportValidFrom == "" ? "" : new Date(reportValidFrom).toISOString());
        formData.append("reportLastIssued", reportLastIssued == null || reportLastIssued == "" ? "" : new Date(reportLastIssued).toISOString());
        formData.append("reportValidityPeriod", reportValidityPeriod || "");
        formData.append("reportValidTo", reportValidTo == null || reportValidTo == "" ? "" : new Date(reportValidTo).toISOString());
      }

      appendCertificateFiles(formData, reportFiles, "report", true);

      await certificateThirdPartyUpdateStore.store(formData, { "Content-Type": "multipart/form-data" });
      fireAlert(
        <>
          <p>{intl.formatMessage({ id: "certificates.successfully_updated_certificates_reports", defaultMessage: "Successfully updated certificate / reports(s)" })}</p>
          <h4>idNotaCERT: {existingCertificate.idNotaCert}</h4>
        </>,
        intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
        "success",
      ).then(() => {
        history.push(`/certificates`);
      });
    } catch (error) {
      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function saveCertificate() {
    if (!isValid()) return;

    setIsSaving(true);

    try {
      const formData = new FormData();

      formData.append("name", name);
      formData.append("notes", notes || "");
      formData.append("code", code);
      formData.append("active", active ? "true" : "false");
      formData.append("typeOfCertificate", typeOfCertificate);

      formData.append("company", selectedCompany);

      formData.append("idTipoDocumental", selectedDocumentType.toString());

      const selectedLegislationsFull = allRegulationsLegislations.filter((l) => selectedLegislations.includes(l.idRegulacion));
      const selectedRegulationsFull = allRegulationsLegislations.filter((l) => selectedRegulations.includes(l.idRegulacion));

      formData.append("legislations", JSON.stringify(selectedLegislationsFull ?? []));
      formData.append("regulations", JSON.stringify(selectedRegulationsFull ?? []));

      formData.append("certificateEntity", certificateEntity || "");
      //formData.append("certificateNumber", certificateNumber || "");
      formData.append("certificateNameOf", certificateNameOf || "");
      formData.append("certificateValidFrom", certificateValidFrom == null || certificateValidFrom == "" ? "" : new Date(certificateValidFrom).toISOString());
      formData.append("certificateLastIssued", certificateLastIssued == null || certificateLastIssued == "" ? "" : new Date(certificateLastIssued).toISOString());
      formData.append("certificateValidityPeriod", certificateValidityPeriod || "");
      formData.append("certificateValidTo", certificateValidTo == null || certificateValidTo == "" ? "" : new Date(certificateValidTo).toISOString());
      formData.append(
        "certificateManufacturingValidity",
        certificateManufacturingValidity == null || certificateManufacturingValidity == "" ? "" : new Date(certificateManufacturingValidity).toISOString(),
      );
      formData.append("certificateWarningPeriod", certificateWarningPeriod || 0);
      formData.append("certificateWarningPeriodType", certificateWarningPeriodType);

      appendCertificateFiles(formData, certificateFiles, "certificate");

      formData.append("reportEntity", reportEntity || "");
      //formData.append("reportNumber", reportNumber || "");
      formData.append("reportNameOf", reportNameOf || "");
      formData.append("reportValidFrom", reportValidFrom == null || reportValidFrom == "" ? "" : new Date(reportValidFrom).toISOString());
      formData.append("reportLastIssued", reportLastIssued == null || reportLastIssued == "" ? "" : new Date(reportLastIssued).toISOString());
      formData.append("reportValidityPeriod", reportValidityPeriod || "");
      formData.append("reportValidTo", reportValidTo == null || reportValidTo == "" ? "" : new Date(reportValidTo).toISOString());

      // reportFiles.forEach((file) => {
      //   formData.append("reportFiles", file);
      // });

      appendCertificateFiles(formData, reportFiles, "report");

      formData.append("series", JSON.stringify(selectedSeries));
      formData.append("products", JSON.stringify(selectedProducts));

      var idNotaCERT = await certificateThirdPartyStore.store(formData, { "Content-Type": "multipart/form-data" });
      fireAlert(
        <>
          <p>{intl.formatMessage({ id: "certificates.successfully_saved_certificates_reports", defaultMessage: "Successfully saved certificate / reports(s)" })}</p>
          <h4>idNotaCERT: {idNotaCERT}</h4>
        </>,
        intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
        "success",
      ).then(() => {
        history.push(`/certificates`);
      });
    } catch (error) {
      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  }

  const isCertificateValid = () => certificateFiles.length === 0 || certificateNameOf !== "";
  const isReportValid = () => reportFiles.length === 0 || reportNameOf !== "";

  const isCertificateFilesValid = () => certificateFiles.every(({ number, language }) => number && language.length > 0);
  const isReportFilesValid = () => reportFiles.every(({ number, language }) => number && language.length > 0);

  function isValid() {
    return (
      isCertificateValid() &&
      isCertificateFilesValid() &&
      isReportValid() &&
      isReportFilesValid() &&
      selectedCompany !== "" &&
      (existingCertificate != null || selectedSeries?.length > 0) &&
      (existingCertificate != null || search != "")
    );
  }

  function performSearch() {
    setValidationCheck(true);
    if (selectedCompany == "") return;
    setSubmittedSearch(search);
    setSubmittedCompany(selectedCompany);
  }

  function handleEnter(e) {
    if (e.keyCode === 13) {
      e.preventDefault();
      performSearch();
    }
  }

  async function generate(keyCompany, series, language, templateId, idTipoDocumental, pdfName) {
    setIsLoading(true);

    try {
      const pdfBytes = await certificateGenerateProvider.get({
        keyCompany: keyCompany,
        series: series,
        language: language,
        templateId: templateId,
        idTipoDocumental: idTipoDocumental,
        nameOfCertificate: pdfName,
      });

      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      link.download = pdfName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  }

  async function saveDocumentType() {
    const name = customDocumentTypeName.trim();
    if (!name || documentTypeExists == name || codeExists || nameExists) return;

    const document = {
      name: name,
      description: customDocumentTypeDescription,
    };
    await certificateDocumentTypeStore.store(document);
    await getDocumentTypes(name);

    setCustomDocumentTypeName("");
    setCustomDocumentTypeDescription("");
    toggleDocumentTypeModal();
  }

  async function lookupDocumentType() {
    const name = customDocumentTypeName.trim();
    const result = await certificateDocumentTypeExistenceProvider.get(name);
    if (result?.exists) {
      if (documentTypeExists != name) {
        fireAlert(
          intl.formatMessage({ id: "certificates.document_type_already_exists", defaultMessage: "Document type already exists" }),
          intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
          "error",
        );
      }
      setDocumentTypeExists(name);
    } else {
      setDocumentTypeExists("");
    }
  }

  async function saveRegulation() {
    const name = customRegulationName.trim();
    if (!name || regulationExists == name || customRegulationAppliesTo.length == 0) return;

    const reg = {
      regulation: name,
      description: customRegulationDescription,
      regulationType: customRegulationType,
      validFrom: new Date(customRegulationValidFrom).toISOString(),
      validTo: new Date(customRegulationValidTo).toISOString(),
      appliesTo: customRegulationAppliesTo.map((r) => r.value),
    };
    await certificateRegulationStore.store(reg);

    setCustomRegulationName("");
    setCustomRegulationDescription("");
    setCustomRegulationAppliesTo([]);
    toggleRegulationModal();
  }

  async function lookupRegulation() {
    const name = customRegulationName?.trim();
    const result = await certificateRegulationExistenceProvider.get(name);
    if (result?.exists) {
      if (regulationExists != name) {
        fireAlert(
          intl.formatMessage({ id: "certificates.regulation_already_exists", defaultMessage: "Regulation already exists" }),
          intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
          "error",
        );
      }
      setRegulationExists(name);
    } else {
      setRegulationExists("");
    }
  }

  async function lookupCertCode() {
    const certCode = code;
    if (certCode && (existingCertificate == null || existingCertificate.code != certCode.trim())) {
      const result = await certificateNotaCodeProvider.get(certCode);
      if (result?.exists) {
        if (codeExists != certCode) {
          fireAlert(
            intl.formatMessage({ id: "certificates.nota_code_already_exists", defaultMessage: "Nota Code already exists" }),
            intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
            "error",
          );
        }
        setCodeExists(certCode);
        return;
      }
    }

    setCodeExists("");
  }

  async function lookupCertName() {
    const certName = name;
    if (certName && (existingCertificate == null || existingCertificate.name != certName.trim())) {
      const result = await certificateNotaNameProvider.get(certName);
      if (result?.exists) {
        if (nameExists != certName) {
          fireAlert(
            intl.formatMessage({ id: "certificates.nota_name_already_exists", defaultMessage: "Nota Name already exists" }),
            intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
            "error",
          );
        }
        setNameExists(certName);
        return;
      }
    }

    setNameExists("");
  }

  function renderUpdateButtonIcon({ submitting, submitted }) {
    if (submitting) {
      return <i className="fas fa-sync fa-pulse"></i>;
    } else if (submitted) {
      return <i className="fas fa-check"></i>;
    } else {
      return <i className="fas fa-save"></i>;
    }
  }

  function renderUpdateButtonText({ submitting, submitted }) {
    if (submitting) {
      return <FormattedMessage id="app.updating" defaultMessage="Updating..." />;
    } else if (submitted) {
      return <FormattedMessage id="app.updated" defaultMessage="Updated" />;
    } else {
      return <FormattedMessage id="app.update" defaultMessage="Update" />;
    }
  }

  function renderUpdateButtonChild(state) {
    return (
      <>
        {renderUpdateButtonIcon(state)} {renderUpdateButtonText(state)}
      </>
    );
  }

  return (
    <>
      <Row>
        <Col sm="6">
          <h3 className="card-title">
            {existingCertificate ? (
              <FormattedMessage id="certificates.edit_certificate" defaultMessage="Edit a Certificate" />
            ) : (
              <FormattedMessage id="certificates.certificate" defaultMessage="Create a Certificate" />
            )}
          </h3>
        </Col>
      </Row>

      {existingCertificate && (
        <div className="mb-4">
          <ExistingCertificateSummary data={existingCertificate}></ExistingCertificateSummary>
        </div>
      )}

      <Row>
        <Col>
          <Row>
            <Col sm={6}>
              <FormGroup row className="mb-1">
                <Label sm={6} for="name">
                  <FormattedMessage id="certificates.certificate_type" defaultMessage="Certificate Type" />
                </Label>
                <Col sm={6}>
                  <select className="form-control" onChange={(e) => setTypeOfCertificate(parseInt(e.target.value))}>
                    <option value="0">{intl.formatMessage({ id: "certificates.product", defaultMessage: "Product" })}</option>
                    <option value="1" disabled={true}>
                      {intl.formatMessage({ id: "certificates.manufacturing_center", defaultMessage: "Manufacturing Center" })}
                    </option>
                  </select>
                </Col>
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup row className="mb-1">
                <Col>
                  {typeOfCertificate === 0 && (
                    <>
                      <select
                        className="form-control"
                        value={selectedDocumentType || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedDocumentType(parseInt(value));
                        }}
                        disabled={typeOfCertificate !== 0}
                      >
                        {documentTypes.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>

                      <div className="mt-2">
                        <Button color="link" size="sm" className="p-0" onClick={() => toggleDocumentTypeModal()}>
                          + {intl.formatMessage({ id: "certificates.add_new_document_type", defaultMessage: "Add new document type" })}
                        </Button>
                      </div>
                    </>
                  )}
                </Col>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col sm={6}>
              <FormGroup row className=" mb-1">
                <Label sm={6} for="code">
                  <FormattedMessage id="certificates.certificate_code" defaultMessage="CERT Code" />
                </Label>
                <Col sm={6}>
                  <input
                    type="text"
                    // className={`form-control ${validationCheck && code == "" ? "is-invalid" : ""}`}
                    className={`form-control ${codeExists ? "is-invalid" : ""}`}
                    id="code"
                    onChange={(e) => setCode(e.target.value)}
                    onBlur={lookupCertCode}
                    value={code}
                    maxLength={100}
                  />
                </Col>
              </FormGroup>

              <FormGroup row className=" mb-1">
                <Label sm={6} for="name">
                  <FormattedMessage id="certificates.certificate_name" defaultMessage="CERT Name" />
                </Label>
                <Col sm={6}>
                  <input
                    type="text"
                    // className={`form-control ${validationCheck && name == "" ? "is-invalid" : ""}`}
                    className={`form-control ${nameExists ? "is-invalid" : ""}`}
                    id="name"
                    onChange={(e) => setName(e.target.value)}
                    onBlur={lookupCertName}
                    value={name}
                    maxLength={100}
                  />
                </Col>
              </FormGroup>

              <FormGroup row className=" mb-1">
                <Label sm={6}>
                  <FormattedMessage id="attribute.active" defaultMessage="Active" />
                </Label>
                <Col sm={6}>
                  <CustomInput
                    type="switch"
                    id={"active"}
                    checked={active}
                    onChange={(e) => {
                      if (e.target.checked == false) {
                        fireConfirm(
                          intl.formatMessage({ id: "certificates.are_you_sure_you_want_to_proceed", defaultMessage: "Are you sure you want to proceed?" }),
                          intl.formatMessage({ id: "certificates.do_you_want_to_disable", defaultMessage: "Do you want to disable?" }),
                          "question",
                          intl.formatMessage({ id: "app.yes", defaultMessage: "Yes" }),
                          intl.formatMessage({ id: "app.no", defaultMessage: "No" }),
                          () => {
                            setActive(false);
                          },
                          () => {
                            setActive(true);
                          },
                        );
                      } else {
                        setActive(e.target.checked);
                      }
                    }}
                  />
                </Col>
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="mt-3">
            <CardHeader>
              <FormattedMessage id="certificates.define_regulations" defaultMessage="Define Regulations" />
            </CardHeader>
            <CardBody>
              <Row>
                <Col sm="6">
                  <h3>
                    <FormattedMessage id="certificates.legislations" defaultMessage="Legislations" />
                  </h3>
                  <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                    {legislations.map((s) => (
                      <div key={s.idRegulacion} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={tickedLegislations.includes(s.idRegulacion)}
                          id={`check-legislation-${s.idRegulacion}`}
                          onClick={() => onLegislationClick(s.idRegulacion)}
                        />
                        <label className={`form-check-label ${s.missing ? "text-primary" : ""}`} for={`check-legislation-${s.idRegulacion}`}>
                          {s.regulacion} {s.regulacionDescripcion ? " - " : ""}
                          {s.regulacionDescripcion} {s.missing ? <i className="fas fa-exclamation-circle"></i> : null}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4"></div>
                </Col>
                <Col sm="6">
                  <h3>
                    <FormattedMessage id="certificates.regulations" defaultMessage="Regulations" />
                  </h3>
                  <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                    {displayRegulations?.map((s) => (
                      <div key={s.idRegulacion} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedRegulations.includes(s.idRegulacion)}
                          id={`check-regulation-${s.idRegulacion}`}
                          onClick={() => onRegulationClick(s.idRegulacion)}
                        />
                        <label className={`form-check-label ${s.missing ? "text-primary" : ""}`} for={`check-regulation-${s.idRegulacion}`}>
                          {s.regulacion} {s.regulacionDescripcion ? " - " : ""}
                          {s.regulacionDescripcion} {s.missing ? <i className="fas fa-exclamation-circle"></i> : null}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4"></div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="mt-2">
                    <Button color="link" size="sm" className="p-0" onClick={() => toggleRegulationModal()}>
                      + {intl.formatMessage({ id: "certificates.add_new_regulation", defaultMessage: "Add new regulation" })}
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col sm="6">
                  {selectedLegislations.map((s) => (
                    <Fragment key={s.idRegulacion}>
                      <span className="badge badge-pill badge-primary">
                        {allRegulationsLegislations.find((r) => r.idRegulacion == s)?.regulacion}{" "}
                        <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onLegislationClick(s, true)}></i>
                      </span>{" "}
                    </Fragment>
                  ))}
                </Col>
                <Col sm="6">
                  {selectedRegulations.map((s) => (
                    <Fragment key={s.idRegulacion}>
                      <span className="badge badge-pill badge-primary">
                        {allRegulationsLegislations.find((r) => r.idRegulacion == s)?.regulacion ?? regulations.find((r) => r.idRegulacion == s)?.regulacion ?? ""}{" "}
                        <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onRegulationClick(s)}></i>
                      </span>{" "}
                    </Fragment>
                  ))}
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>

      <datalist id="entity-list">
        {entities.map((a) => (
          <option key={a.idEntidadEmisora} value={a.entidadEmisora} />
        ))}
      </datalist>
      <datalist id="company-list">
        {companies.map((a) => (
          <option key={a.keyCompanyManufacturer} value={a.companyManufacturer} />
        ))}
      </datalist>

      {(true || !existingCertificate || existingCertificate.typeOfCertificate == 37) && (
        <Row>
          <Col>
            <CollapsibleCard className="my-3" title={intl.formatMessage({ id: "certificates.certificates", defaultMessage: "Certificates" })}>
              <Card>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="certificateEntity">
                          <FormattedMessage id="certificates.certificate_entity" defaultMessage="Certificate Entity" />
                        </Label>
                        {/* <Input id="certificateEntity" type="text" value={certificateEntity} onChange={(e) => setCertificateEntity(e.target.value)} /> */}

                        <Input
                          id="certificateEntity"
                          type="text"
                          list="entity-list"
                          value={certificateEntity}
                          onChange={(e) => setCertificateEntity(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "product_information.select_or_type",
                            defaultMessage: "Select or type value...",
                          })}
                        />
                      </FormGroup>
                    </Col>

                    {/* <Col md={4}>
                      <FormGroup>
                        <Label for="certificateNumber">
                          <FormattedMessage id="certificates.certificate_number" defaultMessage="Certificate Number" />
                        </Label>
                        <Input
                          id="certificateNumber"
                          type="text"
                          value={certificateNumber}
                          disabled={existingCertificate != null}
                          onChange={(e) => setCertificateNumber(e.target.value)}
                        />
                      </FormGroup>
                    </Col> */}

                    <Col md={4}>
                      <FormGroup>
                        <Label for="certificateNameOf">
                          <FormattedMessage id="certificates.under_the_name_of" defaultMessage="Under the name of" /> {"*"}
                        </Label>

                        <Input
                          id="certificateNameOf"
                          type="text"
                          list="company-list"
                          value={certificateNameOf}
                          onChange={(e) => setCertificateNameOf(e.target.value)}
                          className={!isCertificateValid() ? "is-invalid" : ""}
                          placeholder={intl.formatMessage({
                            id: "product_information.select_or_type",
                            defaultMessage: "Select or type value...",
                          })}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="certificateValidFrom">
                          <FormattedMessage id="certificates.valid_from" defaultMessage="Valid From" />
                        </Label>
                        <Input id="certificateValidFrom" type="date" value={certificateValidFrom} onChange={(e) => setCertificateValidFrom(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="certificateLastIssued">
                          <FormattedMessage id="certificates.last_issued" defaultMessage="Last Issued" />
                        </Label>
                        <Input id="certificateLastIssued" type="date" value={certificateLastIssued} onChange={(e) => setCertificateLastIssued(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="certificateValidityPeriod">
                          <FormattedMessage id="certificates.validity_period" defaultMessage="Validity Period" />
                        </Label>
                        <Input id="certificateValidityPeriod" value={certificateValidityPeriod} onChange={(e) => setCertificateValidityPeriod(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="certificateValidTo">
                          <FormattedMessage id="certificates.valid_to" defaultMessage="Valid To" />
                        </Label>
                        <Input id="certificateValidTo" type="date" value={certificateValidTo} onChange={(e) => setCertificateValidTo(e.target.value)} />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="certificateManufacturingValidity">
                          <FormattedMessage id="certificates.manufacturing_validity_date" defaultMessage="Manufacturing Validity Date" />
                        </Label>
                        <Input
                          id="certificateManufacturingValidity"
                          type="date"
                          value={certificateManufacturingValidity}
                          onChange={(e) => setCertificateManufacturingValidity(e.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <Label for="certificateWarningPeriod">
                          <FormattedMessage id="certificates.warning_period" defaultMessage="Warning Period" />
                        </Label>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <Input
                            id="certificateWarningPeriod"
                            type="number"
                            min="1"
                            value={certificateWarningPeriod}
                            onChange={(e) => setCertificateWarningPeriod(parseInt(e.target.value, 10) || "")}
                          />
                          <Input
                            id="certificateWarningPeriodType"
                            type="select"
                            style={{ width: "auto" }}
                            value={certificateWarningPeriodType}
                            onChange={(e) => setCertificateWarningPeriodType(e.target.value)}
                          >
                            <option value="DAY">{intl.formatMessage({ id: "certificates.days", defaultMessage: "Day(s)" })}</option>
                            <option value="MONTH">{intl.formatMessage({ id: "certificates.months", defaultMessage: "Month(s)" })}</option>
                            <option value="YEAR">{intl.formatMessage({ id: "certificates.years", defaultMessage: "Year(s)" })}</option>
                          </Input>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <h3>
                        <FormattedMessage id="certificates.upload_pdfs" defaultMessage="Upload PDFs" />
                      </h3>
                      {certificateFiles.length > 0 && (
                        <>
                          <h3 className="card-title">
                            <FormattedMessage id="certificates.files" defaultMessage="Files" />
                          </h3>
                          <ul>{certificateFileItems}</ul>
                        </>
                      )}
                      <div {...getRootPropsCertificate({ className: "dropzone" })}>
                        <input {...getInputPropsCertificate()} />
                        <button type="button" onClick={openCertificate} className="btn btn-outline-dark">
                          <FormattedMessage id="app.add" defaultMessage="Add" />
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </CollapsibleCard>
          </Col>
        </Row>
      )}
      {(true || !existingCertificate || existingCertificate.typeOfCertificate == 191) && (
        <Row>
          <Col>
            <CollapsibleCard className="my-3" title={intl.formatMessage({ id: "certificates.reports", defaultMessage: "Reports" })}>
              <Card>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="reportEntity">
                          <FormattedMessage id="certificates.report_entity" defaultMessage="Report Entity" />
                        </Label>

                        <Input
                          id="reportEntity"
                          type="text"
                          list="entity-list"
                          value={reportEntity}
                          onChange={(e) => setReportEntity(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "product_information.select_or_type",
                            defaultMessage: "Select or type value...",
                          })}
                        />
                      </FormGroup>
                    </Col>

                    {/* <Col md={4}>
                      <FormGroup>
                        <Label for="reportNumber">
                          <FormattedMessage id="certificates.report_number" defaultMessage="Report Number" />
                        </Label>
                        <Input id="reportNumber" type="text" value={reportNumber} disabled={existingCertificate != null} onChange={(e) => setReportNumber(e.target.value)} />
                      </FormGroup>
                    </Col> */}

                    <Col md={4}>
                      <FormGroup>
                        <Label for="reportNameOf">
                          <FormattedMessage id="certificates.under_the_name_of" defaultMessage="Under the name of" /> {"*"}
                        </Label>
                        <Input
                          id="reportNameOf"
                          type="text"
                          list="company-list"
                          value={reportNameOf}
                          className={!isReportValid() ? "is-invalid" : ""}
                          onChange={(e) => setReportNameOf(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "product_information.select_or_type",
                            defaultMessage: "Select or type value...",
                          })}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="reportValidFrom">
                          <FormattedMessage id="certificates.valid_from" defaultMessage="Valid From" />
                        </Label>
                        <Input id="reportValidFrom" type="date" value={reportValidFrom} onChange={(e) => setReportValidFrom(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="reportLastIssued">
                          <FormattedMessage id="certificates.last_issued" defaultMessage="Last Issued" />
                        </Label>
                        <Input id="reportLastIssued" type="date" value={reportLastIssued} onChange={(e) => setReportLastIssued(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="reportValidityPeriod">
                          <FormattedMessage id="certificates.validity_period" defaultMessage="Validity Period" />
                        </Label>
                        <Input id="reportValidityPeriod" value={reportValidityPeriod} onChange={(e) => setReportValidityPeriod(e.target.value)} />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label for="reportValidTo">
                          <FormattedMessage id="certificates.valid_to" defaultMessage="Valid To" />
                        </Label>
                        <Input id="reportValidTo" type="date" value={reportValidTo} onChange={(e) => setReportValidTo(e.target.value)} />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <h3>
                        <FormattedMessage id="certificates.upload_pdfs" defaultMessage="Upload PDFs" />
                      </h3>
                      {reportFiles.length > 0 && (
                        <>
                          <h3 className="card-title">
                            <FormattedMessage id="certificates.files" defaultMessage="Files" />
                          </h3>
                          <ul>{reportFileItems}</ul>
                        </>
                      )}
                      <div {...getRootPropsReport({ className: "dropzone" })}>
                        <input {...getInputPropsReport()} />
                        <button type="button" onClick={openReport} className="btn btn-outline-dark">
                          <FormattedMessage id="app.add" defaultMessage="Add" />
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </CollapsibleCard>
          </Col>
        </Row>
      )}

      <Row>
        <Col className="mt-3">
          {!existingCertificate && (
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <FormattedMessage id="certificates.search" defaultMessage="Series / Product search" />
                  </CardHeader>
                  <CardBody>
                    <FormGroup row className="my-0">
                      <Label sm={6}>
                        <FormattedMessage id="certificates.production_center" defaultMessage="Production Center / Brand" /> {"*"}
                      </Label>
                      <Col sm={6}>
                        <Input
                          type="select"
                          //className={validationCheck && selectedCompany === "" ? "is-invalid" : ""}
                          value={selectedCompany}
                          onChange={(e) => setSelectedCompany(parseInt(e.target.value))}
                          className={selectedCompany == "" || selectedCompany == null ? "is-invalid" : ""}
                        >
                          <option value="">{intl.formatMessage({ id: "product_information.select_an_option", defaultMessage: "Please select an option" })}</option>
                          {companies.map((a) => (
                            <option key={a.keyCompanyManufacturer} value={a.keyCompanyManufacturer}>
                              {a.companyManufacturer}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </FormGroup>
                    <FormGroup row className="my-0">
                      <Label sm={6}>
                        <FormattedMessage id="certificates.search" defaultMessage="Series / Product search" /> {"*"}
                      </Label>
                      <Col sm={6}>
                        <Input
                          type="text"
                          onKeyDown={handleEnter}
                          className={validationCheck && search == "" ? "is-invalid" : ""}
                          onKeyUp={(e) => setSearch(e.target.value)}
                        ></Input>
                      </Col>
                    </FormGroup>
                  </CardBody>

                  <CardFooter>
                    <Button className="btn btn-outline-primary" onClick={performSearch}>
                      <FormattedMessage id="app.search" defaultMessage="Search" />
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              <CertificateThirdPartySeriesProductSearch
                search={submittedSearch}
                keyCompany={submittedCompany}
                selectedSeries={selectedSeries}
                setSelectedSeries={setSelectedSeries}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                existingCertificate={existingCertificate}
              ></CertificateThirdPartySeriesProductSearch>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card className="mt-3 mb-3">
                <CardBody>
                  {/* <FormGroup row className="my-0">
                    <Label sm={3}>
                      <FormattedMessage id="certificates.language" defaultMessage="Language" />
                    </Label>
                    <Col sm={9}>
                      <MultiSelect
                        valueRenderer={customValueRenderer}
                        options={languages}
                        value={selectedLanguage}
                        hasSelectAll={false}
                        onChange={setSelectedLanguage}
                        labelledBy="Select"
                        overrideStrings={localisedStrings}
                      />
                    </Col>
                  </FormGroup> */}
                  <FormGroup row className="my-0">
                    <Label sm={3}>
                      <FormattedMessage id="certificates.notes" defaultMessage="Notes" />
                    </Label>
                    <Col sm={9}>
                      <Input type="textarea" height={50} defaultValue={notes} onKeyUp={(e) => setNotes(e.target.value)}></Input>
                    </Col>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              {existingCertificate && (
                <SubmitButton className="btn btn-outline-danger" submitting={isSaving} onClick={updateCertificate} disabled={isSaving || !isValid()}>
                  {renderUpdateButtonChild}
                </SubmitButton>
              )}
              {!existingCertificate && (
                <SubmitButton className="btn btn-outline-danger" submitting={isSaving} onClick={saveCertificate} disabled={isSaving || !isValid()}></SubmitButton>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal isOpen={regulationModalOpen} toggle={toggleRegulationModal}>
        <ModalHeader toggle={toggleRegulationModal} className="pb-2">
          <FormattedMessage id="certificates.create_a_regulation" defaultMessage="Create a regulation" />
        </ModalHeader>
        <ModalBody className="py-0">
          <FormGroup>
            <Label for="customRegulationName">
              <FormattedMessage id="certificates.name" defaultMessage="Name" />
            </Label>
            <Input
              type="text"
              id="customRegulationName"
              required={true}
              onBlur={lookupRegulation}
              value={customRegulationName}
              onChange={(e) => setCustomRegulationName(e.target.value)}
              className={`form-control ${regulationExists || customRegulationName == "" ? "is-invalid" : ""}`}
            />
          </FormGroup>
          <FormGroup>
            <Label for="customRegulationDescription">
              <FormattedMessage id="certificates.description" defaultMessage="Description" />
            </Label>
            <Input type="textarea" id="customRegulationDescription" value={customRegulationDescription} onChange={(e) => setCustomRegulationDescription(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>
              <FormattedMessage id="certificates.regulation_type" defaultMessage="Type of regulation" />
            </Label>
            <select
              className="form-control"
              value={customRegulationType}
              onChange={(e) => {
                const value = e.target.value;
                setCustomRegulationType(parseInt(value));
              }}
            >
              {legislationTypes.map((d) => (
                <option key={d.idTipoRegulacion} value={d.idTipoRegulacion}>
                  {d.tipoRegulacion}
                </option>
              ))}
            </select>
          </FormGroup>
          <FormGroup>
            <Label for="customRegulationValidFrom">
              <FormattedMessage id="certificates.valid_from" defaultMessage="Valid From" />
            </Label>
            <Input id="customRegulationValidFrom" type="date" value={customRegulationValidFrom} onChange={(e) => setCustomRegulationValidFrom(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="customRegulationValidTo">
              <FormattedMessage id="certificates.valid_to" defaultMessage="Valid To" />
            </Label>
            <Input id="customRegulationValidTo" type="date" value={customRegulationValidTo} onChange={(e) => setCustomRegulationValidTo(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="customRegulationValidTo">
              <FormattedMessage id="certificates.applies_to" defaultMessage="Applies To" />
            </Label>
            <MultiSelect
              valueRenderer={customValueRenderer}
              options={documentTypes}
              value={customRegulationAppliesTo}
              hasSelectAll={false}
              onChange={setCustomRegulationAppliesTo}
              labelledBy="Select"
              overrideStrings={localisedStrings}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-outline-danger" onClick={saveRegulation}>
            <FormattedMessage id="app.save" defaultMessage="Save" />
          </button>
          <button className="btn btn-outline-dark" onClick={toggleRegulationModal}>
            <FormattedMessage id="app.cancel" defaultMessage="Cancel" />
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={documentTypeModalOpen} toggle={toggleDocumentTypeModal}>
        <ModalHeader toggle={toggleDocumentTypeModal} className="pb-2">
          <FormattedMessage id="certificates.create_a_document" defaultMessage="Create a document type" />
        </ModalHeader>
        <ModalBody className="py-0">
          <FormGroup>
            <Label for="customName">
              <FormattedMessage id="certificates.name" defaultMessage="Name" />
            </Label>
            <Input
              type="text"
              id="customName"
              required={true}
              onBlur={lookupDocumentType}
              value={customDocumentTypeName}
              onChange={(e) => setCustomDocumentTypeName(e.target.value)}
              className={`form-control ${documentTypeExists || customDocumentTypeName == "" ? "is-invalid" : ""}`}
            />
          </FormGroup>
          <FormGroup>
            <Label for="customDescription">
              <FormattedMessage id="certificates.description" defaultMessage="Description" />
            </Label>
            <Input type="textarea" id="customDescription" value={customDocumentTypeDescription} onChange={(e) => setCustomDocumentTypeDescription(e.target.value)} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-outline-danger" onClick={saveDocumentType}>
            <FormattedMessage id="app.save" defaultMessage="Save" />
          </button>
          <button className="btn btn-outline-dark" onClick={toggleDocumentTypeModal}>
            <FormattedMessage id="app.cancel" defaultMessage="Cancel" />
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CertificateThirdPartyProductSelection;
